import { useState, type FormEvent } from "react";
import {
  useActualizarProveedor,
  useCrearProveedor,
  useEliminarProveedor,
  useProveedoresDominio,
  type ProveedorDominio,
} from "../lib/dominios";
import { Aviso, Boton, Campo, Entrada, Modal } from "./ui";

/** Alta, edición y baja de los proveedores donde están contratados los dominios. */
export function GestorProveedores({
  abierto,
  alCerrar,
}: {
  abierto: boolean;
  alCerrar: () => void;
}) {
  const { data } = useProveedoresDominio();
  const crear = useCrearProveedor();
  const actualizar = useActualizarProveedor();
  const eliminar = useEliminarProveedor();

  const [editando, setEditando] = useState<ProveedorDominio | null>(null);
  const [nombre, setNombre] = useState("");
  const [sitio, setSitio] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reiniciar = () => {
    setEditando(null);
    setNombre("");
    setSitio("");
    setError(null);
    crear.reset();
    actualizar.reset();
  };

  function elegir(p: ProveedorDominio) {
    setEditando(p);
    setNombre(p.nombre);
    setSitio(p.sitioWeb ?? "");
    setError(null);
  }

  function guardar(e: FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) {
      setError("Ponle un nombre al proveedor");
      return;
    }
    const body = { nombre: nombre.trim(), sitioWeb: sitio.trim() };
    const opciones = {
      onSuccess: reiniciar,
      onError: (err: Error) => setError(err.message),
    };
    if (editando) actualizar.mutate({ id: editando.id, body }, opciones);
    else crear.mutate(body, opciones);
  }

  return (
    <Modal
      titulo="Proveedores de dominios"
      abierto={abierto}
      alCerrar={() => {
        reiniciar();
        alCerrar();
      }}
      ancho="max-w-xl"
    >
      <div className="space-y-4 p-5">
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          {(data ?? []).length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-slate-500">
              Aún no hay proveedores. Agrega el primero abajo.
            </li>
          )}
          {(data ?? []).map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{p.nombre}</div>
                <div className="text-xs text-slate-500">
                  {p.dominios} dominio{p.dominios === 1 ? "" : "s"}
                  {p.sitioWeb && ` · ${p.sitioWeb}`}
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <Boton variante="fantasma" onClick={() => elegir(p)}>
                  Editar
                </Boton>
                <Boton
                  variante="peligro"
                  disabled={eliminar.isPending}
                  onClick={() =>
                    eliminar.mutate(p.id, { onError: (err) => setError(err.message) })
                  }
                >
                  Borrar
                </Boton>
              </div>
            </li>
          ))}
        </ul>

        {error && <Aviso>{error}</Aviso>}

        <form
          onSubmit={guardar}
          className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-800"
        >
          <p className="text-sm font-medium">
            {editando ? `Editar «${editando.nombre}»` : "Nuevo proveedor"}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Nombre" requerido>
              <Entrada
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="GoDaddy"
              />
            </Campo>
            <Campo etiqueta="Panel / sitio web">
              <Entrada
                value={sitio}
                onChange={(e) => setSitio(e.target.value)}
                placeholder="https://dcc.godaddy.com"
              />
            </Campo>
          </div>
          <div className="flex justify-end gap-2">
            {editando && (
              <Boton type="button" variante="secundario" onClick={reiniciar}>
                Cancelar edición
              </Boton>
            )}
            <Boton type="submit" disabled={crear.isPending || actualizar.isPending}>
              {editando ? "Guardar" : "Agregar"}
            </Boton>
          </div>
        </form>
      </div>
    </Modal>
  );
}
